import { createSelector } from '@reduxjs/toolkit';

import type { SelectOption } from '@/components/UI/Select/Select';
import { _isNotBlank, _isEmpty, _isNil, _isNotEmpty, _notNil, safelyDecodeURIComponent, uuid } from '@/littledash';
import type { ID } from '@/model/Common.model';
import type { Treatment, TreatmentField, TreatmentMethod } from '@/model/Treatment.model';
import type { TreatmentFormField } from './TreatmentForm.model';
import type { MetadataField, MetadataFieldWithValue } from '@/model/Metadata.model';
import { validateMetadataSelectOptions, validMultiSelectValue } from '@/components/Studies/Create/Show.utils';
import type { StudyFormMetadataItem } from '@/model/StudyForm.model';
import { isSubsetOfSets } from '@/utils/browser.utils.ts';
import { type MetadataFieldBuilderReturn } from '@/components/UI/FormFields/FormField.utils';
import { updateNumberToString } from '@/components/Studies/Treatments/Treatment.utils';
import { featuresSelector } from '@/support/Selectors';

export interface TreatmentLabelFieldValues {
  doseValue?: string | null;
  doseUnit?: string | null;
  volumeValue?: string | null;
  volumeUnit?: string | null;
  doseVolumeValue?: string | null;
  doseVolumeUnit?: string | null;
}

/**
 * Formats a numeric value for display in treatment labels.
 * Matches backend TreatmentDisplayNameUtils.buildDisplayName() formatting.
 */
export const formatDisplayValue = (value: string | null | undefined): string | null => {
  if (!_isNotBlank(value)) {
    return null;
  }

  const numericValue = parseFloat(value);
  if (isNaN(numericValue) || numericValue === 0) {
    return null;
  }

  if (numericValue < 1 && numericValue > 0.01) {
    return numericValue.toFixed(3);
  }

  if (numericValue >= 1 && numericValue < 1_000_000) {
    return numericValue === Math.floor(numericValue) ? String(Math.floor(numericValue)) : numericValue.toFixed(3);
  }

  return numericValue.toExponential(3);
};

/**
 * Checks if a value and unit combination is valid for display.
 */
const isValidDisplayValue = (value: string | null | undefined, unit: string | null | undefined): boolean => {
  const formattedValue = formatDisplayValue(value);
  return _notNil(formattedValue) && _isNotBlank(unit);
};

/**
 * Builds a display name from treatment name, value, and unit.
 * Matches backend TreatmentDisplayNameUtils.buildDisplayName().
 * Only includes value/unit in parentheses if both are valid.
 */
const buildDisplayName = (
  treatmentName: string,
  value: string | null | undefined,
  unit: string | null | undefined
): string => {
  const formattedValue = formatDisplayValue(value);
  if (!_notNil(formattedValue) || !_isNotBlank(unit)) {
    return treatmentName;
  }

  return `${treatmentName} (${formattedValue} ${unit})`.trim();
};

/**
 * Generates a treatment label based on treatment name, dosing method, and field values.
 * Matches backend TreatmentDisplayNameUtils.buildDisplayNameByMethodType() logic.
 */
export const generateTreatmentLabel = (
  treatmentName: string | null | undefined,
  dosingMethod: TreatmentMethod,
  fields: TreatmentLabelFieldValues
): string => {
  if (!_isNotBlank(treatmentName)) {
    return '';
  }

  const { doseValue, doseUnit, volumeValue, volumeUnit, doseVolumeValue, doseVolumeUnit } = fields;

  switch (dosingMethod) {
    case 'volume':
      return buildDisplayName(treatmentName, volumeValue, volumeUnit);

    case 'dose_volume':
      if (isValidDisplayValue(doseValue, doseUnit)) {
        return buildDisplayName(treatmentName, doseValue, doseUnit);
      }
      return buildDisplayName(treatmentName, doseVolumeValue, doseVolumeUnit);

    case 'concentration':
    case 'concentration_mlkg':
    case 'concentration_vgkg':
      return buildDisplayName(treatmentName, doseValue, doseUnit);

    case 'custom':
      if (isValidDisplayValue(doseValue, doseUnit)) {
        return buildDisplayName(treatmentName, doseValue, doseUnit);
      }
      return buildDisplayName(treatmentName, volumeValue, volumeUnit);

    default:
      return treatmentName;
  }
};

export const dosingMethods: Record<TreatmentMethod, SelectOption<TreatmentMethod>> = {
  volume: {
    value: 'volume',
    label: 'Standard volume',
  },
  dose_volume: {
    value: 'dose_volume',
    label: 'Dose volume (ml/kg)',
  },
  concentration: {
    value: 'concentration',
    label: 'Standard concentration',
  },
  concentration_mlkg: {
    value: 'concentration_mlkg',
    label: 'Standard concentration (ml/kg)',
  },
  concentration_vgkg: {
    value: 'concentration_vgkg',
    label: 'Standard concentration (vg/kg)',
  },
  custom: {
    value: 'custom',
    label: 'Custom',
  },
};

export const dosingMethodOptions = Object.values(dosingMethods);

export const getDefaults = (
  formName: string,
  formFields: Array<TreatmentFormField>,
  currentTreatmentMethod: TreatmentMethod,
  currentTreatment?: Treatment
): Record<string, Record<string, unknown>> => ({
  [formName]:
    _isNotEmpty(currentTreatment) && currentTreatmentMethod == currentTreatment.type
      ? buildDefaultsFromTreatment(currentTreatment)
      : buildEmptyFormDefaults(formFields),
});

const buildDefaultsFromTreatment = (treatment: Treatment): Record<string, unknown> =>
  treatment.fields.reduce<Record<string, unknown>>((acc, field) => {
    acc[field.name] = {
      ...field,
      default_value: field.default_display_value ?? field.default_value,
      unit: {
        name: field.unit.display_unit ?? field.unit.name,
        id: field.unit.display_unit_id ?? field.unit.id,
        unit_type: field.name,
      },
    };
    return acc;
  }, {});

const buildEmptyFormDefaults = (formFields: Array<TreatmentFormField>): Record<string, unknown> =>
  formFields.reduce<Record<string, unknown>>((acc, field) => {
    acc[field.name] = {
      default_value: field.default_value,
      unit: field.units[0],
      required: field.required,
    };
    return acc;
  }, {});

export interface TreatmentMetadataValidationResult {
  invalidFields: Record<string, { title: string; slug: string }>;
  hasInvalidFields: boolean;
}

export const isValidMetadataValue = (value: unknown, metadataField: MetadataField): boolean => {
  const fieldType = metadataField.field_type;

  // Only validate select/multi_select fields with options
  if ((fieldType !== 'select' && fieldType !== 'multi_select') || _isEmpty(metadataField.options)) {
    return true;
  }

  // If no value, it's valid (required validation is separate)
  if (_isEmpty(value)) {
    return true;
  }

  // Decode options as they may also be URL-encoded
  const decodedOptions = new Set(metadataField.options?.map((opt) => safelyDecodeURIComponent(opt)));

  if (fieldType === 'multi_select') {
    const valuesToCheck = parseMultiSelectValue(value);
    const selectedValues = new Set(valuesToCheck.map((v) => safelyDecodeURIComponent(String(v))));

    // Check if all selected values are valid options
    return isSubsetOfSets(selectedValues, decodedOptions);
  }

  // Single select - decode URL-encoded value before comparison
  return decodedOptions.has(safelyDecodeURIComponent(String(value)));
};

const parseMultiSelectValue = (value: unknown): Array<unknown> => {
  if (Array.isArray(value)) {
    return value;
  }

  if (typeof value === 'string') {
    // Try to parse as JSON array if it looks like one
    if (value.startsWith('[') && value.endsWith(']')) {
      try {
        const parsed = JSON.parse(value);
        if (Array.isArray(parsed)) {
          return parsed;
        } else {
          // Parsed but not an array - treat original string as single value
          return [value];
        }
      } catch {
        // Failed to parse - treat as single value (e.g., a literal string starting with '[')
        return [value];
      }
    } else {
      // Plain string - treat as single selected value
      return [value];
    }
  }

  // Other types - wrap in array and validate
  return [value];
};

export const validateTreatmentMetadata = (
  treatment: Treatment | null,
  metadata: Array<MetadataField>
): TreatmentMetadataValidationResult => {
  const invalid: Record<string, { title: string; slug: string }> = {};

  if (!_notNil(treatment) || !_isNotEmpty(treatment.metadata)) {
    return {
      invalidFields: {},
      hasInvalidFields: false,
    };
  }

  const metadataMap: Record<number, MetadataField> = {};
  metadata.forEach((meta) => {
    metadataMap[Number(meta.id)] = meta;
  });

  treatment.metadata.forEach((treatmentMeta) => {
    const metadataField = metadataMap[Number(treatmentMeta.glossary_id)];

    if (_notNil(metadataField)) {
      const fieldType = metadataField.field_type;

      if ((fieldType === 'select' || fieldType === 'multi_select') && _isNotEmpty(metadataField.options)) {
        const metadataItem: StudyFormMetadataItem = {
          info: metadataField as MetadataFieldWithValue,
          required: metadataField.required ?? false,
        };

        // Parse JSON string values for multi_select fields
        let valueToValidate = treatmentMeta.value;
        if (fieldType === 'multi_select' && typeof treatmentMeta.value === 'string') {
          try {
            valueToValidate = JSON.parse(treatmentMeta.value);
          } catch {
            // If parsing fails, keep original value
          }
        }

        const hasInvalidOption = validateMetadataSelectOptions(metadataItem, valueToValidate);

        if (hasInvalidOption) {
          invalid[metadataField.slug] = { title: metadataField.title, slug: metadataField.slug };
        }
      }
    }
  });

  return {
    invalidFields: invalid,
    hasInvalidFields: _isNotEmpty(Object.keys(invalid)),
  };
};

export const featureSelector = createSelector([featuresSelector], (features) => {
  return {
    diseaseInductionEnabled: features?.disease_induction ?? false,
    treatmentLabelEditEnabled: features?.treatment_label_edit ?? false,
  };
});

interface TreatmentExtended extends Treatment {
  meta: Record<string, string>;
}

export const handleTreatmentSubmit = (
  treatmentData: Treatment & Record<string, TreatmentField>,
  metaFieldValues: Array<MetadataFieldBuilderReturn>,
  treatment: Treatment,
  edit: boolean,
  onSubmit: (updatedTreatment: Treatment, edit: boolean, treatmentId?: ID) => void
) => {
  const dosingMethodValues = treatmentData[treatmentData.type];
  delete treatmentData[treatmentData.type];

  const multiSelectFields = metaFieldValues.reduce((acc, metaField) => {
    if (metaField.type === 'multi_select' || metaField.type === 'lookup_multi_select') {
      acc.add(metaField.slug);
    }
    return acc;
  }, new Set<string>());

  // If multi & lookup multi select fields have no values, remove them before creating treatment meta
  const meta = Object.entries(treatmentData?.meta ?? {}).reduce<Record<string, string>>((acc, [key, value]) => {
    if (multiSelectFields.has(key) && !validMultiSelectValue(value)) {
      return acc;
    }
    return { ...acc, [key]: value };
  }, {});

  const updatedTreatment: TreatmentExtended = { temp_id: treatment?.temp_id ?? uuid(), ...treatmentData, meta };

  if (_isNotEmpty(dosingMethodValues)) {
    updatedTreatment.fields = updateNumberToString([...Object.values(dosingMethodValues)]);
  }

  onSubmit(updatedTreatment, edit, treatment?.api_id);

  return updatedTreatment;
};
